import { type ErrorInfo } from 'react';
import { isRouteErrorResponse } from 'react-router';

import { isDynamicImportLoadError } from '@/errorHandling/dynamicImportError.util.ts';
import { captureAppException } from '@/monitoring/sentry.util.ts';

type RouterErrorInfo = {
  errorInfo?: ErrorInfo;
};

const componentStacks = new WeakMap<object, string>();

/**
 * `RouterProvider`'s `onError` runs from `componentDidCatch` before the
 * error element's effects, so the component stack it receives is stashed
 * here for the backend report built inside the error element.
 */
export const rememberComponentStack = (error: unknown, componentStack?: string): void => {
  if (componentStack && typeof error === 'object' && error !== null) {
    componentStacks.set(error, componentStack);
  }
};

export const recallComponentStack = (error: unknown): string | undefined =>
  typeof error === 'object' && error !== null ? componentStacks.get(error) : undefined;

/**
 * `<RouterProvider onError>` handler (OCD-304): reports router-level
 * errors (view render, lazy load, loader) to Sentry with the same payload
 * the global error boundary used — the error, the component stack (render
 * errors only) and the `react-error-boundary` context name.
 *
 * - Router 404s are rendered as the not-found screen by the route error
 *   element instead of being reported.
 * - Stale dynamic-import failures outside local env skip Sentry; the route
 *   error element sends the backend report and reloads the page, matching
 *   the previous boundary behaviour.
 */
export const handleRouterError = (error: unknown, info: RouterErrorInfo): void => {
  const componentStack = info.errorInfo?.componentStack ?? undefined;
  rememberComponentStack(error, componentStack);

  if (isRouteErrorResponse(error) && error.status === 404) {
    return;
  }

  if (import.meta.env.VITE_ENVIRONMENT !== 'local' && isDynamicImportLoadError(error)) {
    return;
  }

  captureAppException(error, {
    componentStack,
    contextName: 'react-error-boundary',
  });
};
